// Clients — Office records. Desktop, a table, a detail panel.
//
//   GET    /api/clients                       list (+ property_count, job_count)
//   POST   /api/clients                       create
//   PUT    /api/clients/:id                   edit / deactivate
//   DELETE /api/clients/:id                   delete (clients.delete)
//   GET    /api/clients/properties/all        every property, filtered here by client
//   GET    /api/settings/default_tax_rate     the company rate a blank tax field inherits
//
// Tax: `default_tax_rate` is nullable ON PURPOSE (server/utils/clientTaxSchema.js).
// NULL means "inherit the company rate", 0 means "deliberately zero-rated". The
// form therefore asks which of the three states is meant instead of offering a
// number box that cannot tell blank from zero, and sends `null` for inherit.

import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { post, put, del, serverSentence } from '../../shared/http';
import { useCan, useRole } from '../../../hooks/usePermission';
import { DataTable, Notice, Pill, Sheet, FwButton, FwField, FwInput, FwTextarea, FwSelect, Skeleton, EmptyState } from '../../../ui/fw';
import { taxRateHint, rateToInputValue, formatRate } from '../../../utils/taxRateLabel';
import { fmtMoney, todayIso } from '../shared/format';
import { invoicePill, invoiceStatusKey } from '../shared/status';
import { copyText } from '../shared/download';
import {
  useOfficeQuery, useAction, PageHeader, Toolbar, SearchBox, Panel, Split, DetailList, DetailRow,
  ActivePill, LoadError, FormError, FormRow, SheetFooter, ConfirmSheet, matches, textOr, factFallback, fmtDate, fmtDateTime,
  useRecordView, recordById, DetailActions, ACTIVE_VALUES, ACTIVE_LABELS, matchesActive, readFilter,
} from './OfficePage';
import AddressField from '../../shared/AddressField';
import PropertyForm from './PropertyForm';
import ClientContacts from './ClientContacts';

/**
 * Who the SERVER lets read one client's invoices.
 *
 * `GET /api/invoices/by-client/:clientId` is `checkRole('admin', 'manager')`
 * (server/routes/invoices.js:224), and `checkRole` tests
 * `allowedRoles.includes(req.user.role)` and nothing else
 * (server/middleware/auth.js:117) — there is no `extra_permissions` escape and
 * no `invoices` permission that opens it. So the gate here is the ROLE, not
 * `useCan('invoices', 'read')`.
 *
 * screens/Invoices.js gates the whole-list screen on the permission against
 * the same kind of role-only handler; that mismatch is known and is not
 * copied here, because on THIS screen accounting is a first-class visitor
 * (navigation.js lets manager, accounting and admin at Clients) and a panel
 * gated on the permission would load, be refused, and read as an error every
 * single time for that role.
 */
export const INVOICE_ROLES = ['admin', 'manager'];
export const canReadClientInvoices = (role) => INVOICE_ROLES.includes(role);

const TAX_MODES = [
  { value: 'inherit', label: 'Inherit the company rate' },
  { value: 'zero', label: 'Zero-rated (0%, company rate does not apply)' },
  { value: 'custom', label: 'Custom rate for this client' },
];

function taxModeOf(client) {
  if (!client || client.default_tax_rate === null || client.default_tax_rate === undefined || client.default_tax_rate === '') return 'inherit';
  return Number(client.default_tax_rate) === 0 ? 'zero' : 'custom';
}

/** What the table and detail panel say about a client's tax. */
export function taxSummary(client, companyRate) {
  if (!client) return '';
  if (client.tax_exempt === 1 || client.tax_exempt === true) return 'Tax exempt';
  const mode = taxModeOf(client);
  if (mode === 'zero') return 'Zero-rated';
  if (mode === 'custom') return `${formatRate(client.default_tax_rate)} (own rate)`;
  return companyRate === null || companyRate === undefined ? 'Inherits company rate (none set)' : `Inherits ${formatRate(companyRate)}`;
}

/** The body the form sends. Exported so the null-vs-zero rule is unit-testable. */
export function clientBody(form, editing) {
  let rate = null;
  if (form.tax_mode === 'zero') rate = 0;
  else if (form.tax_mode === 'custom') rate = parseFloat(form.tax_rate);
  const body = {
    name: form.name.trim(),
    email: form.email.trim(),
    phone: form.phone.trim(),
    address: form.address,
    notes: form.notes,
    default_tax_rate: rate,
    tax_exempt: !!form.tax_exempt,
  };
  if (editing) body.is_active = !!form.is_active;
  return body;
}

function seed(client) {
  const mode = taxModeOf(client);
  return {
    name: client?.name || '',
    email: client?.email || '',
    phone: client?.phone || '',
    address: client?.address || '',
    notes: client?.notes || '',
    tax_mode: mode,
    tax_rate: mode === 'custom' ? rateToInputValue(client.default_tax_rate) : '',
    tax_exempt: !!client?.tax_exempt,
    is_active: client ? !!client.is_active : true,
  };
}

function ClientForm({ open, client, companyRate, onClose, onSaved }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(() => seed(client));
  const { busy, error, run, setError } = useAction();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const hintInput = form.tax_mode === 'inherit' ? '' : form.tax_mode === 'zero' ? '0' : form.tax_rate;
  const hint = taxRateHint({ taxExempt: form.tax_exempt, rateInput: hintInput, companyRate });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Give the client a name.'); return; }
    if (form.tax_mode === 'custom') {
      const n = parseFloat(form.tax_rate);
      if (!Number.isFinite(n) || n < 0 || n > 100) { setError('Enter a rate between 0 and 100, or choose another tax option.'); return; }
    }
    const body = clientBody(form, !!client);
    const ok = await run(async () => {
      if (client) await put(`/clients/${client.id}`, body);
      else await post('/clients', body);
      await qc.invalidateQueries({ queryKey: ['clients'] });
    }, 'Could not save the client.');
    if (ok && onSaved) onSaved();
  };

  return (
    <Sheet
      open={open}
      title={client ? 'Edit client' : 'New client'}
      onClose={onClose}
      footer={<SheetFooter onCancel={onClose} busy={busy} formId="or-client-form" submitLabel={client ? 'Save changes' : 'Create client'} />}
    >
      <form id="or-client-form" className="or-form" onSubmit={submit} noValidate>
        <FormError error={error} />
        <FwField label="Name" required htmlFor="or-client-name">
          <FwInput value={form.name} onChange={set('name')} autoComplete="organization" />
        </FwField>
        <FormRow>
          <FwField label="Email" htmlFor="or-client-email">
            <FwInput type="email" value={form.email} onChange={set('email')} autoComplete="email" />
          </FwField>
          <FwField label="Phone" htmlFor="or-client-phone">
            <FwInput type="tel" value={form.phone} onChange={set('phone')} autoComplete="tel" />
          </FwField>
        </FormRow>
        {/* One line, so the FORMATTED address is what belongs in it — this is
            the address that gets printed on an invoice. Plain text input with
            no Maps key, which is the default install. */}
        <AddressField
          label="Billing address"
          htmlFor="or-client-address"
          value={form.address}
          onChange={set('address')}
          onPlace={(p) => setForm((f) => ({ ...f, address: p.formatted || p.street }))}
        />
        <FwField label="Notes" htmlFor="or-client-notes">
          <FwTextarea value={form.notes} onChange={set('notes')} rows={3} />
        </FwField>
        <FwField label="Sales tax" hint={hint} htmlFor="or-client-tax-mode">
          <FwSelect value={form.tax_mode} onChange={set('tax_mode')} disabled={form.tax_exempt}>
            {TAX_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </FwSelect>
        </FwField>
        {form.tax_mode === 'custom' && !form.tax_exempt ? (
          <FwField label="Rate (%)" required htmlFor="or-client-tax-rate">
            <FwInput type="number" min="0" max="100" step="0.01" inputMode="decimal" value={form.tax_rate} onChange={set('tax_rate')} />
          </FwField>
        ) : null}
        <label className="or-form__check">
          <input type="checkbox" checked={form.tax_exempt} onChange={(e) => setForm((f) => ({ ...f, tax_exempt: e.target.checked }))} />
          Tax exempt
        </label>
        {client ? (
          <label className="or-form__check">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
            Active
          </label>
        ) : null}
      </form>
    </Sheet>
  );
}

// A property, as a card: which place, where it is, is it still live, how much
// work has happened there. Four columns, four slots — nothing is hidden here
// because there is nothing spare.
const PROP_COLUMNS = [
  { key: 'name', label: 'Property', role: 'title' },
  // Subtitle, so the same rule as the client card above: a property with no
  // street and no town said "—" on the line under its own name.
  { key: 'address', label: 'Address', role: 'subtitle', render: (p) => factFallback([[p.address, p.city].filter(Boolean).join(', ')], 'No address on file') },
  { key: 'job_count', label: 'Jobs', align: 'right', role: 'fact', render: (p) => p.job_count ?? 0 },
  { key: 'is_active', label: 'Status', role: 'badge', render: (p) => <ActivePill active={p.is_active} /> },
];

/**
 * ClientInvoices — this client's billing history, in the detail panel.
 *
 *   GET /api/invoices/by-client/:clientId   admin | manager (see INVOICE_ROLES)
 *
 * The handler matches on `client_id = ?` OR `client_name = (SELECT name FROM
 * clients WHERE id = ?)`, so an old invoice raised before the client record
 * existed still appears as long as the typed name still matches. It excludes
 * estimates (`COALESCE(is_estimate, 0) = 0`) and returns `SELECT *`, newest
 * first.
 *
 * ── Money: nothing here does arithmetic ─────────────────────────────────────
 *
 * The one money figure rendered is the server's own `invoices.total`, handed
 * to `fmtMoney` (shared/format.js: `Intl.NumberFormat`, no arithmetic). No
 * subtotal + tax − discount recombination, no balance, and deliberately NO
 * TOTAL ROW: the endpoint sends rows and no client total, and summing them
 * here would be new money math on a figure someone would read back to a
 * client (CLAUDE.md rule 4). The classic screen does sum them
 * (components/Clients/Clients.js:509, `reduce`); that figure is not
 * reproduced, and the note under the table says what is missing instead of
 * leaving a blank for the next session to helpfully fill in.
 *
 * ── A count is a measurement ────────────────────────────────────────────────
 *
 * Same rule as the properties panel below, and the classic screen learnt it
 * the same way: the heading may carry `(n)` only when the request answered.
 * A refused list must not read as "no invoices for this client yet", because
 * that sentence is what someone acts on when they are asking whether a client
 * has ever been billed.
 */
function ClientInvoices({ client }) {
  const navigate = useNavigate();
  const role = useRole();
  const allowed = canReadClientInvoices(role);
  const q = useOfficeQuery(['client-invoices', client.id], `/invoices/by-client/${client.id}`, { enabled: allowed });
  const today = todayIso();
  const invoices = useMemo(() => q.data || [], [q.data]);
  const counted = allowed && !q.error && !q.isPending;

  // On a phone this is the answer to "has this client been billed, and is it
  // paid?" — so the number identifies the card, the pill answers the question
  // in the top-right, and the amount stays a LABELLED fact. An unlabelled
  // money figure in a card slot is the one thing that must not happen here:
  // a bare "$1,240.50" beside a date could be read as a balance or a payment.
  const columns = useMemo(() => [
    { key: 'invoice_number', label: 'Invoice', role: 'title', render: (i) => <strong>{textOr(i.invoice_number)}</strong> },
    // `invoices.created_at` is a bare SQLite CURRENT_TIMESTAMP with no zone
    // marker (server/database.js:319). fmtDateTime parses it as UTC — read as
    // local it shows TOMORROW for anything billed after 8pm Eastern, which is
    // the date an operator reads back to a client querying a bill.
    { key: 'created_at', label: 'Raised', role: 'subtitle', render: (i) => fmtDateTime(i.created_at) },
    { key: 'status', label: 'Status', role: 'badge', render: (i) => { const p = invoicePill(i, today); return <Pill tone={p.tone} data-status={invoiceStatusKey(i, today)}>{p.label}</Pill>; } },
    { key: 'total', label: 'Amount', align: 'right', role: 'fact', render: (i) => fmtMoney(i.total) },
    /**
     * The row's actions — docs/PARITY_2026-09-08.md §21.
     *
     * The classic record had "Copy invoice link" and "View public invoice" per
     * row (`git show d25258b^:client/src/components/Clients/Clients.js` L498).
     * The rebuilt panel had neither, and the copy control lived on
     * InvoiceDetail two navigations away — which is two too many for somebody
     * on the phone to a client asking for their bill again.
     *
     * `access_token` has always been on this answer: the handler is a bare
     * `SELECT *` (server/routes/invoices.js:295). Measured on a scratch server
     * on 2026-09-09 — by-client/1 answers `access_token: 787a677b…`. It is the
     * token, never the id, that addresses `/invoice/:token`; building the link
     * from the id would hand somebody a 404 to send to a client.
     *
     * Nullable, so both public controls disappear together rather than
     * rendering `/invoice/null` — a link that looks live and is not is worse
     * than no link, because it gets forwarded.
     *
     * NO FIGURE IS READ HERE. `i.id` and `i.access_token` only; the amount is
     * still the server's own `total`, still unsummed, in the column above.
     */
    {
      key: 'actions',
      label: 'Actions',
      role: 'fact',
      render: (i) => (
        <span className="of-inline">
          <FwButton variant="quiet" icon="eye" data-action="open-invoice" onClick={() => navigate(`/invoices/${i.id}`)}>Open</FwButton>
          {i.access_token ? (
            <a
              className="of-link"
              href={`/invoice/${i.access_token}`}
              target="_blank"
              rel="noreferrer"
              data-action="public-invoice"
            >Public page</a>
          ) : null}
          {i.access_token ? (
            <FwButton
              variant="quiet"
              icon="copy"
              data-action="copy-invoice-link"
              onClick={() => copyText(`${window.location.origin}/invoice/${i.access_token}`)}
            >Copy link</FwButton>
          ) : null}
        </span>
      ),
    },
  ], [today, navigate]);

  if (!allowed) {
    return (
      <Panel className="or-panel--flush" title="Invoices">
        <p className="or-note">
          Reading one client&apos;s invoices is limited to admins and managers on the server,
          so nothing is listed here. It is not that there are none.
        </p>
      </Panel>
    );
  }

  return (
    <Panel className="or-panel--flush" title={counted ? `Invoices (${invoices.length})` : 'Invoices'}>
      {/* `serverSentence`, not OfficePage's LoadError, because `describeError`
          flattens every 403 to "You do not have permission to do that." and
          the handler's own body names the door that was shut. Same Notice, so
          it reads identically to the properties panel above it. */}
      {q.error ? (
        <Notice tone="stop" className="or-error">
          {serverSentence(q.error, "Could not load this client's invoices.")}
        </Notice>
      ) : null}
      {q.isPending ? <Skeleton lines={2} /> : null}
      {/* NO `onRowClick`, and that is load-bearing rather than an omission.
          RecordList renders a card per record below 768px of CONTAINER width,
          and with onRowClick set that card is a real <button> whose children
          are <span>s — so the row controls above would be buttons inside a
          button, an invalid content model. "Open" is the same navigation,
          named, and OfficeClients.test.js's "no nested interactive control"
          case fails if onRowClick comes back while the actions are in a cell.

          And this is NOT a phone-only concern, which is the part worth
          writing down. This panel lives in the client detail column, ~380px
          wide, so RecordList draws CARDS here even on a 1440px desktop.
          Measured on 2026-09-09: setting `role: 'hidden'` on the actions
          column made the controls disappear at 1440px as well as 390px, and
          `role` is read only by the card branch. jsdom disagrees — its
          innerWidth is 1024 and there is no ResizeObserver, so the unit tests
          see a table — which is exactly the blind spot the browser check
          exists for. */}
      <DataTable
        dense
        columns={columns}
        rows={invoices}
        empty={q.error
          ? 'The invoice list could not be loaded, so this is not a list of none — check before raising one.'
          : q.isPending ? 'Loading…' : 'No invoices for this client yet.'}
        caption={`Invoices for ${client.name}`}
      />
      {counted && invoices.length ? (
        <p className="or-note">
          Each amount is that invoice&apos;s own total as billed. They are not added up here —
          the server does not send a client total.
        </p>
      ) : null}
    </Panel>
  );
}

/**
 * `propertiesFailed` / `propertiesPending` are the properties QUERY's state, not
 * this client's data.
 *
 * `properties` is `propsQ.data || []` filtered by client, so a refused or
 * still-in-flight list is indistinguishable from a client who has none — and
 * this panel spends that ambiguity twice, both times in the affirmative:
 * "Properties (0)" as a heading, and "all 0 of its properties" inside the
 * delete confirmation. The first invites someone to re-add a property that
 * already exists; the second is a false statement about what a destructive
 * button is going to remove, made directly above it. The clients table beside
 * it renders `property_count` from a DIFFERENT request, so the two can sit on
 * screen disagreeing.
 *
 * A count may only be shown when something was counted (docs/AGENT_LEARNINGS.md,
 * 2026-08-17: "(0)" reads as arithmetic, and arithmetic is trusted).
 */
function ClientDetail({ client, properties, propertiesFailed, propertiesPending, companyRate, canUpdate, canCreate, canDelete, full, onToggleFull, onEdit, onClose, onChanged }) {
  const navigate = useNavigate();
  // "View jobs" only for someone who can read jobs: clients and jobs are
  // separate grants, and a clients-only extra would land on a refusal (#711).
  const canSeeJobs = useCan('jobs', 'read');
  const counted = !propertiesFailed && !propertiesPending;
  const qc = useQueryClient();
  const clients = useMemo(() => [{ id: client.id, name: client.name }], [client.id, client.name]);
  const [propSheet, setPropSheet] = useState(null); // { property } | null
  const [confirm, setConfirm] = useState(false);
  const { busy, error, run } = useAction();

  const toggleActive = () => run(async () => {
    await put(`/clients/${client.id}`, { is_active: !client.is_active });
    await qc.invalidateQueries({ queryKey: ['clients'] });
  }, 'Could not update the client.');

  const remove = async () => {
    const ok = await run(async () => {
      await del(`/clients/${client.id}`);
      await qc.invalidateQueries({ queryKey: ['clients'] });
      await qc.invalidateQueries({ queryKey: ['properties'] });
    }, 'Could not delete the client.');
    if (ok) { setConfirm(false); onChanged(); }
  };

  return (
    <Panel
      title={client.name}
      actions={<DetailActions full={full} onToggleFull={onToggleFull} onClose={onClose} />}
    >
      <LoadError error={error ? { message: error } : null} />
      <DetailList>
        <DetailRow label="Status"><ActivePill active={client.is_active} /></DetailRow>
        <DetailRow label="Email">{textOr(client.email)}</DetailRow>
        <DetailRow label="Phone">{textOr(client.phone)}</DetailRow>
        <DetailRow label="Address">{textOr(client.address)}</DetailRow>
        <DetailRow label="Sales tax">{taxSummary(client, companyRate)}</DetailRow>
        {/* The count opens the Jobs list filtered to this client — owner,
            2026-09-26: "Where do I filter jobs by client!?" */}
        <DetailRow label="Jobs">
          {Number(client.job_count) > 0 && canSeeJobs ? (
            <span className="of-inline">
              {client.job_count}
              <FwButton variant="quiet" icon="eye" data-action="view-client-jobs" onClick={() => navigate(`/jobs?client=${client.id}`)}>View jobs</FwButton>
            </span>
          ) : (client.job_count ?? 0)}
        </DetailRow>
        <DetailRow label="Since">{fmtDate(client.created_at)}</DetailRow>
        {/* docs/PARITY_2026-09-08.md §14 — GET /api/clients has selected
            `u.name as created_by_name` (server/routes/clients.js:48) since it
            was written and nothing read it. "Who put this client on the books"
            is the same audit question the estimate and invoice Record cards
            answer; here it is one row rather than a card, because a client
            record has exactly one event. `textOr` so a client created before
            the column existed reads as "—" and not as a blank row. */}
        <DetailRow label="Added by">{textOr(client.created_by_name)}</DetailRow>
        {client.notes ? <DetailRow label="Notes">{client.notes}</DetailRow> : null}
      </DetailList>
      {canUpdate || canDelete ? (
        <div className="or-actions">
          {canUpdate ? <FwButton variant="ghost" icon="edit" onClick={onEdit}>Edit</FwButton> : null}
          {canUpdate ? (
            <FwButton variant="ghost" onClick={toggleActive} loading={busy}>
              {client.is_active ? 'Deactivate' : 'Reactivate'}
            </FwButton>
          ) : null}
          {canDelete ? <FwButton variant="stop" icon="trash" onClick={() => setConfirm(true)}>Delete</FwButton> : null}
        </div>
      ) : null}

      <Panel
        className="or-panel--flush"
        title={counted ? `Properties (${properties.length})` : 'Properties'}
        actions={canCreate ? <FwButton variant="ghost" icon="plus" onClick={() => setPropSheet({ property: null })}>Add property</FwButton> : null}
      >
        {propertiesFailed ? <LoadError error={propertiesFailed} fallback="Could not load this client's properties." /> : null}
        {propertiesPending ? <Skeleton lines={2} /> : null}
        <DataTable
          dense
          columns={PROP_COLUMNS}
          rows={properties}
          empty={propertiesFailed
            ? 'The property list could not be loaded, so this is not a list of none — check before adding one.'
            : propertiesPending ? 'Loading…' : 'No properties yet.'}
          onRowClick={canUpdate ? (p) => setPropSheet({ property: p }) : undefined}
          caption={`Properties for ${client.name}`}
        />
      </Panel>

      <ClientInvoices client={client} />

      <ClientContacts client={client} canUpdate={canUpdate} />

      <PropertyForm
        open={!!propSheet}
        property={propSheet ? propSheet.property : null}
        clientId={client.id}
        clients={clients}
        onClose={() => setPropSheet(null)}
        onSaved={() => setPropSheet(null)}
      />
      <ConfirmSheet
        open={confirm}
        title={`Delete ${client.name}?`}
        confirmLabel="Delete client"
        busy={busy}
        onConfirm={remove}
        onClose={() => setConfirm(false)}
      >
        <p>{counted
          ? `This removes the client and all ${properties.length} of its properties.`
          : 'This removes the client and every property on it — the property list could not be loaded, so this cannot say how many that is.'} Jobs keep their history but lose the client link.</p>
        <p>If they might come back, deactivate instead.</p>
      </ConfirmSheet>
    </Panel>
  );
}

export default function Clients() {
  const canCreate = useCan('clients', 'create');
  const canUpdate = useCan('clients', 'update');
  const canDelete = useCan('clients', 'delete');
  const clientsQ = useOfficeQuery(['clients'], '/clients');
  const propsQ = useOfficeQuery(['properties'], '/clients/properties/all');
  const rateQ = useOfficeQuery(['setting', 'default_tax_rate'], '/settings/default_tax_rate');
  const companyRate = useMemo(() => {
    const n = parseFloat(rateQ.data && rateQ.data.value);
    return Number.isFinite(n) ? n : null;
  }, [rateQ.data]);

  const [q, setQ] = useState('');
  // The open client and its width live in the URL — OfficePage.js
  // useRecordView. A reload keeps the person on the record they were working.
  const view = useRecordView();
  const [sheet, setSheet] = useState(null); // { client } | null

  // The Active filter lives in the URL, like every select on Jobs, Invoices,
  // Estimates and Payroll — so "the inactive ones" survives a reload and can
  // be sent to somebody. The free-text search stays in state: a half-typed
  // word is not a place anybody wants to come back to.
  const [params, setParams] = useSearchParams();
  const active = readFilter(params, 'active', ACTIVE_VALUES);
  const setActive = (value) => {
    const next = new URLSearchParams(params);
    if (value) next.set('active', value); else next.delete('active');
    setParams(next, { replace: true });
  };

  const clients = useMemo(() => clientsQ.data || [], [clientsQ.data]);
  const rows = useMemo(
    () => clients.filter((c) => matchesActive(c, active) && matches(c, ['name', 'email', 'phone', 'address'], q)),
    [clients, q, active],
  );
  const filtersOn = !!(q || active);
  const clearFilters = () => { setActive(''); setQ(''); };
  const selected = recordById(clients, view.openId);
  const selectedProps = useMemo(
    () => (propsQ.data || []).filter((p) => selected && p.client_id === selected.id),
    [propsQ.data, selected]
  );
  const hasInvoices = clients.some((c) => c.invoice_count !== undefined);

  /**
   * The columns, and what a CARD makes of them on a phone.
   *
   * Somebody opening Clients on a phone is looking a client up — by name, to
   * ring them or to see whether they are still a client. So: the name is the
   * headline, the phone number is the line under it (the contact you can act
   * on standing in a driveway; the email is not), Active/Inactive is the
   * badge, and the two counts that say how much of this client there is are
   * the facts.
   *
   * Hidden, and why — `hidden` is table-only, never lost, and each of these is
   * also on the detail panel:
   *   email  a long string that would truncate to "pat@hend…" in a 9rem fact
   *          slot and tell nobody anything; the phone is the useful contact.
   *   tax    a billing rule, read when raising an invoice, not when scanning a
   *          list. It also renders a Pill, and two pills on one card compete
   *          with the status badge that APP_REBUILD.md §3.3 says must be the
   *          thing you see first.
   *   invoice_count
   *          the third count, and the one that is billing-side rather than
   *          "who is this client". It is also CONDITIONAL — the column only
   *          exists when the server happens to send the field — so as a fact
   *          it would make the card change height depending on the payload.
   *          Measured at a 320px viewport the fact grid is a single column, so
   *          a third fact is a third ROW: the card goes from 173px to about
   *          220px and a phone screen holds three clients instead of four.
   *          The invoices a client has are counted, properly, in the detail
   *          panel that lists them.
   */
  const columns = useMemo(() => {
    const cols = [
      { key: 'name', label: 'Client', role: 'title' },
      { key: 'email', label: 'Email', role: 'hidden', render: (c) => textOr(c.email) },
      // The SUBTITLE, so it is the line under the client's name and it may
      // never be a dash. Measured at 320px on 2026-09-11: a client with no
      // phone on file rendered "—" there, directly under the name, on the
      // screen somebody opens in order to make contact.
      //
      // Phone still wins — this screen is opened to ring somebody standing in
      // a driveway — then the email, then the billing address, because any of
      // the three is a way to reach them and a dash is none. The email is
      // still `hidden` as a COLUMN (it truncates to nothing in a fact slot);
      // that decision is about the fact grid and is untouched. Here it is the
      // fallback for one line that would otherwise be blank.
      {
        key: 'phone',
        label: 'Phone',
        role: 'subtitle',
        render: (c) => factFallback([c.phone, c.email, c.address], 'No contact details'),
      },
      { key: 'property_count', label: 'Properties', align: 'right', role: 'fact', render: (c) => c.property_count ?? 0 },
      { key: 'job_count', label: 'Jobs', align: 'right', role: 'fact', render: (c) => c.job_count ?? 0 },
    ];
    if (hasInvoices) cols.push({ key: 'invoice_count', label: 'Invoices', align: 'right', role: 'hidden', render: (c) => c.invoice_count ?? 0 });
    cols.push({ key: 'tax', label: 'Tax', role: 'hidden', render: (c) => (c.tax_exempt ? <Pill tone="warn">Exempt</Pill> : taxSummary(c, companyRate)) });
    cols.push({ key: 'is_active', label: 'Status', role: 'badge', render: (c) => <ActivePill active={c.is_active} /> });
    return cols;
  }, [hasInvoices, companyRate]);

  const table = clientsQ.isPending ? <Skeleton lines={6} /> : clientsQ.error ? (
    <LoadError error={clientsQ.error} fallback="Could not load clients." />
  ) : clients.length === 0 ? (
    <EmptyState icon="clients" title="No clients yet" action={canCreate ? <FwButton icon="plus" onClick={() => setSheet({ client: null })}>New client</FwButton> : null}>
      Add the first client and their properties will follow.
    </EmptyState>
  ) : (
    <Panel className="or-panel--flush">
      <DataTable
        columns={columns}
        rows={rows}
        onRowClick={(c) => view.open(c.id)}
        empty={filtersOn ? 'Nothing matches the search or the filter above.' : 'No clients.'}
        caption="Clients"
      />
    </Panel>
  );

  return (
    <div className="or-page" data-screen="office-Clients">
      {/* `N of M`, the form Jobs, Crew and Users already use. A bare "4
          clients" stayed put over a list the search had narrowed to one — the
          same defect Crew's header carried until 2026-09-10, and now this
          screen has a filter as well as a search to narrow it. */}
      <PageHeader title="Clients" meta={clientsQ.data ? `${rows.length} of ${clients.length}` : null}>
        {canCreate ? <FwButton icon="plus" onClick={() => setSheet({ client: null })}>New client</FwButton> : null}
      </PageHeader>
      {/* A search box over a list that is not on the page is a control that
          appears to do nothing. In full screen the record is the page. */}
      {view.full ? null : (
        <Toolbar>
          <SearchBox value={q} onChange={setQ} placeholder="Search clients" label="Search clients" />
          {/* Out in the open rather than inside MoreFilters: it is the only
              filter this screen has, and a drawer holding one control costs a
              press to reveal what the press was meant to save room for. */}
          <FwSelect aria-label="Filter by status" value={active} onChange={(e) => setActive(e.target.value)}>
            {ACTIVE_VALUES.map((v) => <option key={v || 'any'} value={v}>{ACTIVE_LABELS[v]}</option>)}
          </FwSelect>
          {filtersOn ? (
            <FwButton variant="ghost" className="or-toolbar__clear" onClick={clearFilters}>Clear filters</FwButton>
          ) : null}
        </Toolbar>
      )}
      <Split
        full={view.full}
        main={table}
        aside={selected ? (
          <ClientDetail
            client={selected}
            properties={selectedProps}
            propertiesFailed={propsQ.error || null}
            propertiesPending={propsQ.isPending}
            companyRate={companyRate}
            canUpdate={canUpdate}
            canCreate={canCreate}
            canDelete={canDelete}
            full={view.full}
            onToggleFull={view.toggleFull}
            onEdit={() => setSheet({ client: selected })}
            onClose={view.close}
            onChanged={view.close}
          />
        ) : null}
      />
      {sheet ? (
        <ClientForm
          open
          client={sheet.client}
          companyRate={companyRate}
          onClose={() => setSheet(null)}
          onSaved={() => setSheet(null)}
        />
      ) : null}
    </div>
  );
}
